import { type ReactNode, Fragment, useMemo } from 'react';
import { cn } from '@/shared/utils/cn';
import type { NewHomeContentWidth } from '../../domain/models/NewHomePageData';
import type { NewHomeSectionPresentationVm } from '../viewModels/newHome.vm';
import HeroVideoSection from './HeroVideoSection';
import GridSection from './GridSection';
import CarouselSection from './CarouselSection';
import ParallaxCarouselSection from './ParallaxCarouselSection';
import ReelsSection from './ReelsSection';
import RecommendedSection from './RecommendedSection';
import HomePageSectionViewTracker from './HomePageSectionViewTracker';
import StoreSelectionsSection from '@/features/posCart/presentation/components/StoreSelectionsSection';
import type { PosCartItem } from '@/features/posCart/domain/models/PosCartItem';
import type {
  HeroPresentationVm,
  GridPresentationVm,
  CarouselPresentationVm,
  ParallaxCarouselPresentationVm,
  ReelsPresentationVm,
  RecommendedPresentationVm,
} from '../viewModels/newHome.vm';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';
import type { PlpFiltersResult } from '@/features/plp/domain/models/ProductListing';
import type { RecommendedBrowsePageResult } from '@/features/newHome/application/services/NewHomeService';

interface NewHomeSectionRendererProps {
  sections: NewHomeSectionPresentationVm[];
  getRecommendedProducts: (
    tags: string,
    pageNumber: number,
    options?: { isFiltered?: boolean; signal?: AbortSignal }
  ) => Promise<RecommendedBrowsePageResult>;
  fetchBrowseFilterPreview: (params: {
    tags: string;
    tags_to_sort_filters?: string;
  }) => Promise<PlpFiltersResult>;
  posCartItems?: PosCartItem[];
  showStoreSelections?: boolean;
  onLoadMoreStoreSelections?: () => void;
  hasMoreStoreSelections?: boolean;
}

function SectionWidthWrap({
  contentWidth,
  children,
}: {
  contentWidth?: NewHomeContentWidth;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'w-full',
        contentWidth === 'container' && 'mx-auto max-w-7xl',
        contentWidth === 'gutter' && 'md:px-[5%]',
      )}
    >
      {children}
    </div>
  );
}

function RenderSection({
  section,
  getRecommendedProducts,
  fetchBrowseFilterPreview,
}: {
  section: NewHomeSectionPresentationVm;
  getRecommendedProducts: (
    tags: string,
    pageNumber: number,
    options?: { isFiltered?: boolean; signal?: AbortSignal }
  ) => Promise<RecommendedBrowsePageResult>;
  fetchBrowseFilterPreview: (params: {
    tags: string;
    tags_to_sort_filters?: string;
  }) => Promise<PlpFiltersResult>;
}) {
  const { view, key, eventLabel } = section;

  switch (view.type) {
    case 'hero':
      return (
        <HeroVideoSection
          data={view as HeroPresentationVm}
          eventLabel={eventLabel}
        />
      );
    case 'grid':
      return (
        <GridSection
          data={view as GridPresentationVm}
          sectionKey={key}
          eventLabel={eventLabel}
        />
      );
    case 'carousel':
      return (
        <CarouselSection
          data={view as CarouselPresentationVm}
          sectionKey={key}
          eventLabel={eventLabel}
        />
      );
    case 'parallaxCarousel':
      return (
        <ParallaxCarouselSection
          data={view as ParallaxCarouselPresentationVm}
          sectionKey={key}
          eventLabel={eventLabel}
        />
      );
    case 'reels':
      return (
        <ReelsSection
          data={view as ReelsPresentationVm}
          sectionKey={key}
          eventLabel={eventLabel}
        />
      );
    case 'recommended':
      return (
        <RecommendedSection
          data={view as RecommendedPresentationVm}
          eventLabel={eventLabel}
          getRecommendedProducts={getRecommendedProducts}
          fetchBrowseFilterPreview={fetchBrowseFilterPreview}
        />
      );
    default:
      return null;
  }
}

export default function NewHomeSectionRenderer({
  sections,
  getRecommendedProducts,
  fetchBrowseFilterPreview,
  posCartItems = [],
  showStoreSelections = false,
  onLoadMoreStoreSelections,
  hasMoreStoreSelections,
}: NewHomeSectionRendererProps) {
  const isMobile = useIsMobile();
  const { homePageVariant: pageVariant } = useMultivariateExperiment();

  const visibleSections = sections.filter((s) => {
    if ((pageVariant === 'V3' || pageVariant === 'V6' || pageVariant === 'V7') && s.view.type === 'reels') {
      return false;
    }
    if ((pageVariant === 'V4' || pageVariant === 'V5') && s.key === 'shopTheLook') {
      return false;
    }
    return isMobile ? s.showInMobile : s.showInDesktop;
  });

  const heroSection = visibleSections.find((s) => s.view.type === 'hero');
  const postHeroSections = visibleSections.filter((s) => s.view.type !== 'hero');

  const gradientColor = heroSection
    ? (heroSection.view as HeroPresentationVm).gradientColor
    : undefined;

  // Gradient flows from the hero's edge color at the top, fading to white over ~800px
  const postHeroBg = gradientColor
    ? { background: `linear-gradient(to bottom, ${gradientColor} 0%, #ffffff 800px)` }
    : undefined;

  const filteredStoreCartItems = useMemo(() => {
    if (pageVariant === 'V4') {
      return posCartItems.filter(item => item.sourceType === 'POS_CART' || item.sourceType === 'POS_VIEWED' || item.sourceType === 'WEB_WISHLIST');
    }
    if (pageVariant === 'V5') {
      return posCartItems.filter(item => item.sourceType === 'POS_CART' || item.sourceType === 'POS_VIEWED' || item.sourceType === 'WEB_CART');
    }
    return [];
  }, [posCartItems, pageVariant]);

  const handpickedSectionIndex = postHeroSections.findIndex(s => s.key === 'handpicked');
  const insertionIndex = handpickedSectionIndex !== -1 ? handpickedSectionIndex : 0;

  return (
    <>
      {heroSection && (
        <SectionWidthWrap key={`${heroSection.key}-${heroSection.priority}`} contentWidth={heroSection.contentWidth}>
          <HomePageSectionViewTracker sectionKey={heroSection.key} viewType={heroSection.view.type}>
            <RenderSection
              section={heroSection}
              getRecommendedProducts={getRecommendedProducts}
              fetchBrowseFilterPreview={fetchBrowseFilterPreview}
            />
          </HomePageSectionViewTracker>
        </SectionWidthWrap>
      )}

      {/* Sections wrapper — gradient background starts at hero's dominant color and fades to white */}
      <div style={postHeroBg}>
        {postHeroSections.map((section, idx) => (
          <Fragment key={`${section.key}-${section.priority}`}>
            <SectionWidthWrap contentWidth={section.contentWidth}>
              <HomePageSectionViewTracker sectionKey={section.key} viewType={section.view.type}>
                <RenderSection
                  section={section}
                  getRecommendedProducts={getRecommendedProducts}
                  fetchBrowseFilterPreview={fetchBrowseFilterPreview}
                />
              </HomePageSectionViewTracker>
            </SectionWidthWrap>

            {/* Insert Store selections section below the handpicked section (above recommended section) */}
            {showStoreSelections && idx === insertionIndex && filteredStoreCartItems.length > 0 && (
              <HomePageSectionViewTracker sectionKey="storeSelections" viewType="grid">
                <StoreSelectionsSection
                  items={filteredStoreCartItems}
                  pageName="Home"
                  onLoadMore={onLoadMoreStoreSelections}
                  hasMore={hasMoreStoreSelections}
                />
              </HomePageSectionViewTracker>
            )}
          </Fragment>
        ))}
      </div>
    </>
  );
}
